import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Search, BookOpen, FileText, HelpCircle } from "lucide-react";
import { useEffect } from "react";
import NavigationBar from "@/components/landing/NavigationBar";
import Footer from "@/components/landing/Footer";

const Help = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Check if we need to scroll to a specific section
    if (location.state && location.state.scrollToId) {
      const element = document.getElementById(location.state.scrollToId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [location]);

  const helpTopics = [
    {
      title: "Vendor Competition Analysis",
      description: "Learn how to analyze competitive landscapes and compare vendors",
      icon: <Search className="h-10 w-10 text-blue-400" />,
      items: ["Competitor identification", "Feature comparison", "Pricing analysis", "Market positioning"]
    },
    {
      title: "Supplier Research Guides",
      description: "Comprehensive guides for researching and evaluating suppliers",
      icon: <BookOpen className="h-10 w-10 text-blue-400" />,
      items: ["Supplier vetting", "Quality assessment", "Supply chain analysis", "Risk evaluation"]
    },
    {
      title: "Report Interpretation",
      description: "Understanding the metrics and insights in your market reports",
      icon: <FileText className="h-10 w-10 text-blue-400" />,
      items: ["Market size interpretation", "Growth trend analysis", "Competitive positioning", "Strategic recommendations"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <NavigationBar />
      
      <div className="container mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-white hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-500">
            Vendor Competition Guides
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Resources to help you master competitive analysis and get the most out of AnalystX's market intelligence platform.
          </p>
        </div>
        
        {/* Search bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input 
              type="text" 
              placeholder="Search help articles..." 
              className="w-full px-4 py-3 pl-12 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        {/* Help topics grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {helpTopics.map((topic, index) => (
            <div key={index} className="bg-gray-800/30 rounded-xl p-6 border border-gray-700/30 hover:border-blue-500/30 transition-all duration-300">
              <div className="bg-gray-800/50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                {topic.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{topic.title}</h3>
              <p className="text-gray-400 mb-4">{topic.description}</p>
              <ul className="space-y-2">
                {topic.items.map((item, idx) => (
                  <li key={idx} className="flex items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* FAQ section */}
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center space-x-4 mb-6">
            <HelpCircle className="h-6 w-6 text-blue-400" />
            <h2 className="text-2xl font-semibold">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-800/20 rounded-lg p-6 border border-gray-700/30">
              <h3 className="text-lg font-medium mb-2">How do I export my research reports?</h3>
              <p className="text-gray-300">
                You can export any report by clicking the PDF export button that appears in the top-right corner of your completed research reports.
              </p>
            </div>
            
            <div className="bg-gray-800/20 rounded-lg p-6 border border-gray-700/30">
              <h3 className="text-lg font-medium mb-2">How accurate is the competitive analysis?</h3>
              <p className="text-gray-300">
                Our AI analyzes thousands of data points from reliable sources to provide accurate competitive intelligence. We recommend validating critical insights with primary research when making major business decisions.
              </p>
            </div>
            
            <div className="bg-gray-800/20 rounded-lg p-6 border border-gray-700/30">
              <h3 className="text-lg font-medium mb-2">How often is the market data updated?</h3>
              <p className="text-gray-300">
                Our platform continuously ingests and processes new data. Most market information is updated daily, while specialized industry reports may be refreshed weekly or monthly depending on the sector.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Help;
